import { HttpClient } from 'api/http-client';
import { ProjectId, Team, TeamWithMeta } from 'api/models';

type CreateTeamPayload = {
  roleId?: number;
  teamType?: string;
  teamName: string;
};

type CreateProjectTeamPayload = CreateTeamPayload & {
  projectId: number;
};

export type AssignPlaceholderPayload = {
  teamId: number;
  teamName?: string;
  projectId: number;
  userList?: {
    userId: number;
  }[];
  companyList?: {
    companyId: number;
  }[];
  roleId?: number;
};

export type BulkAssignResponse = {
  teams: {
    team: {
      teamId: number;
      roleId?: number;
      userList: {
        userId: number;
      }[];
    };
  }[];
};

export interface TeamReplacementProps {
  projectId: number;
  teamId: number;
  userId: number;
  roleId: number;
}
export interface TeamReplacementWithCompanyProps {
  projectId: number;
  teamId: number;
  companyId: number;
  roleId: number;
}

class TeamsHttpClient extends HttpClient {
  public async createTeam(payload: CreateTeamPayload): Promise<Team> {
    return this.post('/teams', payload);
  }

  public async createProjectTeam(
    payload: CreateProjectTeamPayload,
  ): Promise<Team> {
    return this.post('/teams', payload);
  }

  public async updateTeam(
    teamId: number,
    payload: Partial<CreateTeamPayload>,
  ): Promise<Team> {
    return this.put(`/teams/${teamId}`, payload);
  }

  public async deleteTeam(teamId: number): Promise<Team> {
    return this.delete(`/teams/${teamId}`);
  }

  public async deleteTeamsByRole(roleId: number): Promise<void> {
    return this.delete(`/teams/roles/${roleId}`);
  }

  public async bulkAssignPlaceholder(
    payload: AssignPlaceholderPayload[],
  ): Promise<BulkAssignResponse> {
    return this.put('/teams/bulk-action', payload);
  }

  public async assignPlaceholder(
    teamId: number,
    payload: Omit<AssignPlaceholderPayload, 'teamId'>,
  ): Promise<Team> {
    return this.put(`/teams/${teamId}`, payload);
  }

  public async getProjectPlaceholders(projectId: ProjectId): Promise<Team[]> {
    const [, _projectId] = projectId;

    return this.get(`/teams?projectIds=${_projectId}`);
  }

  public async getProjectPlaceholdersWithMeta(
    projectId: ProjectId,
  ): Promise<TeamWithMeta[]> {
    const [, _projectId] = projectId;

    return this.get(`/teams?projectIds=${_projectId}&meta=true`);
  }

  public async getAllTeamsInAccount(): Promise<Team[]> {
    return this.get('/teams?projectIds=all');
  }
}

export const TeamsAPI = new TeamsHttpClient();
